import React, { useState, useEffect } from 'react';
import { Sidebar } from './Dashboard';
import BenchmarkTabs from './BenchmarkTabs';
import { CODE_DESCRIPTIONS, CODE_ORDER } from '../codes';
import { API } from '../App';

// Games & Benchmarks: each game's setup (shuffle method, decks, rules — chosen
// from lists the super admin maintains), its Benchmark Mode (a time-boxed
// Benchmark Period whose tests set the benchmark, then freeze it), and custom
// games (copies of an official game for the casino's own comparisons; they never
// change a benchmark and are measured against the official game's).

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#0f0f1a', display: 'flex' },
  main: { flex: 1, padding: '40px', overflowY: 'auto' },
  pageTitle: { color: '#ffffff', fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' },
  pageSubtitle: { color: '#a0a0b0', fontSize: '14px', marginBottom: '24px', maxWidth: '780px', lineHeight: 1.5 },
  sectionTitle: { color: '#ffffff', fontSize: '18px', fontWeight: 'bold', margin: '32px 0 12px' },
  card: { backgroundColor: '#16213e', borderRadius: '16px', padding: '24px', marginBottom: '20px' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' },
  cardTitle: { color: '#ffffff', fontSize: '20px', fontWeight: 'bold', marginBottom: '6px' },
  badgeOn: {
    display: 'inline-block', padding: '4px 10px', borderRadius: '6px', backgroundColor: '#e94560',
    color: '#ffffff', fontSize: '12px', fontWeight: 'bold', letterSpacing: '1px', marginBottom: '10px', marginRight: '6px',
  },
  badgeOff: {
    display: 'inline-block', padding: '4px 10px', borderRadius: '6px', backgroundColor: '#0f3460',
    color: '#a0a0b0', fontSize: '12px', fontWeight: 'bold', letterSpacing: '1px', marginBottom: '10px', marginRight: '6px',
  },
  badgeCustom: {
    display: 'inline-block', padding: '4px 10px', borderRadius: '6px', backgroundColor: '#5b3a8c',
    color: '#ffffff', fontSize: '12px', fontWeight: 'bold', letterSpacing: '1px', marginBottom: '10px', marginRight: '6px',
  },
  statusLine: { color: '#d0d0e0', fontSize: '14px', lineHeight: 1.5 },
  muted: { color: '#a0a0b0', fontSize: '13px' },
  progressOuter: { height: '8px', backgroundColor: '#0f3460', borderRadius: '4px', marginTop: '12px', overflow: 'hidden', maxWidth: '420px' },
  progressInner: { height: '100%', backgroundColor: '#e94560' },
  actions: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  primaryBtn: {
    padding: '10px 20px', backgroundColor: '#e94560', border: 'none', borderRadius: '8px',
    color: '#ffffff', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer',
  },
  secondaryBtn: {
    padding: '10px 20px', backgroundColor: '#0f3460', border: '1px solid #0f3460', borderRadius: '8px',
    color: '#ffffff', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer',
  },
  smallBtn: {
    padding: '6px 12px', backgroundColor: '#0f3460', border: 'none', borderRadius: '6px',
    color: '#ffffff', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer',
  },
  dangerBtn: {
    padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid #e94560', borderRadius: '8px',
    color: '#e94560', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer',
  },
  setupBox: { marginTop: '16px', padding: '12px 14px', backgroundColor: '#0f1a33', borderRadius: '10px' },
  setupHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '8px' },
  chip: {
    display: 'inline-block', padding: '4px 10px', borderRadius: '14px', backgroundColor: '#0f3460',
    color: '#d0d0e0', fontSize: '12px', margin: '0 6px 6px 0',
  },
  panel: { marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #0f3460' },
  label: { color: '#a0a0b0', fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '6px', display: 'block' },
  input: {
    padding: '10px 12px', backgroundColor: '#0f3460', border: '1px solid #0f3460', borderRadius: '8px',
    color: '#ffffff', fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  },
  formRow: { display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' },
  explain: { color: '#d0d0e0', fontSize: '13px', lineHeight: 1.5, marginBottom: '16px', maxWidth: '680px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '16px' },
  stat: { backgroundColor: '#0f3460', borderRadius: '10px', padding: '14px' },
  statNum: { color: '#ffffff', fontSize: '22px', fontWeight: 'bold' },
  statLbl: { color: '#a0a0b0', fontSize: '12px', marginTop: '4px' },
  warning: {
    backgroundColor: 'rgba(255, 204, 0, 0.12)', border: '1px solid #ffcc00', borderRadius: '8px',
    color: '#ffcc00', fontSize: '13px', padding: '10px 14px', marginBottom: '16px', lineHeight: 1.5,
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    color: '#a0a0b0', fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase',
    padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #0f3460',
  },
  td: { color: '#ffffff', fontSize: '14px', padding: '10px 12px', borderBottom: '1px solid #0f3460' },
  tdSub: { color: '#c0c0d0', fontSize: '13px', padding: '6px 12px 6px 32px', borderBottom: '1px solid #13254a' },
  success: { color: '#00cc00', fontSize: '14px', marginTop: '16px' },
  error: { color: '#e94560', fontSize: '14px', marginBottom: '16px' },
};

const STATUS_LABEL = { qualified: 'Qualified', in_progress: 'In progress', not_started: 'Not started' };
const STATUS_COLOR = { qualified: '#00cc00', in_progress: '#ffcc00', not_started: '#e94560' };

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function setupChips(snapshot) {
  const fields = snapshot?.fields || {};
  return Object.values(fields).map(f => `${f.label}: ${Array.isArray(f.value) ? f.value.join(', ') : f.value}`);
}

function SetupSummary({ snapshot }) {
  const chips = setupChips(snapshot);
  if (!chips.length && !snapshot?.notes) return <div style={styles.muted}>Not set yet.</div>;
  return (
    <div>
      {chips.map(c => <span key={c} style={styles.chip}>{c}</span>)}
      {snapshot?.notes && <div style={{ ...styles.muted, marginTop: '4px' }}>Notes: {snapshot.notes}</div>}
    </div>
  );
}

function cellLabel(c) {
  return [c.v1, c.v2, c.v4].filter(Boolean).join(' · ');
}

function codeSort(a, b) {
  const ia = CODE_ORDER.indexOf(a.code), ib = CODE_ORDER.indexOf(b.code);
  return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
}

async function readError(res, fallback) {
  try {
    const data = await res.json();
    if (typeof data.detail === 'string') return data.detail;
    if (data.detail && data.detail.message) return data.detail.message;
    return fallback;
  } catch (e) {
    return fallback;
  }
}

function GameCard({ game, fields, token, isAdmin, onChanged }) {
  const [periods, setPeriods] = useState(null);
  const [panel, setPanel] = useState(null); // start | coverage | extend | cancel | setup | review | history | rename
  const [coverage, setCoverage] = useState(null);
  const [shiftFilter, setShiftFilter] = useState('All');
  const [days, setDays] = useState(30);
  const [label, setLabel] = useState('');
  const [reason, setReason] = useState('');
  const [draft, setDraft] = useState({});
  const [notes, setNotes] = useState('');
  const [setupWarning, setSetupWarning] = useState('');
  const [preview, setPreview] = useState(null);
  const [showCells, setShowCells] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [historyValues, setHistoryValues] = useState({});
  const [newName, setNewName] = useState(game.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const headers = { Authorization: `Bearer ${token}` };
  const jsonHeaders = { ...headers, 'Content-Type': 'application/json' };
  const custom = game.is_custom;

  async function loadPeriods() {
    if (custom) { setPeriods({ open: null, current_benchmark: null, history: [] }); return; }
    const res = await fetch(`${API}/admin/games/${game.id}/benchmark-periods`, { headers });
    if (res.ok) setPeriods(await res.json());
  }

  useEffect(() => { loadPeriods(); }, [game.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = periods?.open;
  const current = periods?.current_benchmark;

  function flash(msg) {
    setMessage(msg);
    setTimeout(() => setMessage(''), 4000);
  }

  function togglePanel(name) {
    setError('');
    setSetupWarning('');
    setConfirmClose(false);
    if (panel === name) { setPanel(null); return; }
    setPanel(name);
    if (name === 'coverage' && open) {
      setCoverage(null);
      fetch(`${API}/admin/benchmark-periods/${open.id}/coverage`, { headers })
        .then(r => r.json()).then(setCoverage).catch(() => {});
    }
    if (name === 'start') {
      setDays(30);
      setLabel(`${new Date().getFullYear()} ${game.name}`);
      setReason('');
    }
    if (name === 'extend' && open) setDays(open.target_days);
    if (name === 'setup') {
      const s = game.setup || {};
      const d = {};
      fields.forEach(f => { d[f.field_key] = s[f.field_key] || (f.multi ? [] : ''); });
      setDraft(d);
      setNotes(s.notes || '');
    }
    if (name === 'review' && open) {
      setPreview(null);
      fetch(`${API}/admin/benchmark-periods/${open.id}/preview`, { method: 'POST', headers })
        .then(async r => {
          if (r.ok) setPreview(await r.json());
          else setError(await readError(r, 'Could not load the review'));
        })
        .catch(() => setError('Could not connect to server'));
    }
    if (name === 'rename') setNewName(game.name);
  }

  async function call(url, method, body, okMsg, after) {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(url, { method, headers: body ? jsonHeaders : headers, body: body ? JSON.stringify(body) : undefined });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (okMsg) flash(typeof okMsg === 'function' ? okMsg(data) : okMsg);
        if (after) await after(data);
      } else {
        setError(await readError(res, 'Something went wrong'));
      }
    } catch (e) {
      setError('Could not connect to server');
    }
    setBusy(false);
  }

  function refreshAll() {
    setPanel(null);
    loadPeriods();
    onChanged();
  }

  async function saveSetup(confirm) {
    setBusy(true);
    setError('');
    const setup = {};
    fields.forEach(f => {
      const v = draft[f.field_key];
      if (f.multi ? (v && v.length) : v) setup[f.field_key] = v;
    });
    try {
      const res = await fetch(`${API}/admin/games/${game.id}/setup`, {
        method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ setup, notes, confirm }),
      });
      if (res.ok) {
        setSetupWarning('');
        flash('Setup saved');
        refreshAll();
      } else if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        setSetupWarning(data.detail?.message || 'This change may affect the benchmark.');
      } else {
        setError(await readError(res, 'Could not save the setup'));
      }
    } catch (e) {
      setError('Could not connect to server');
    }
    setBusy(false);
  }

  function toggleMulti(key, id) {
    setDraft(prev => {
      const cur = prev[key] || [];
      return { ...prev, [key]: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] };
    });
  }

  function loadHistoryValues(periodId) {
    if (historyValues[periodId]) {
      setHistoryValues(p => { const n = { ...p }; delete n[periodId]; return n; });
      return;
    }
    fetch(`${API}/admin/benchmark-periods/${periodId}/results`, { headers })
      .then(r => r.json())
      .then(data => setHistoryValues(p => ({ ...p, [periodId]: data })))
      .catch(() => {});
  }

  if (!periods) {
    return (
      <div style={styles.card}>
        <div style={styles.cardTitle}>{game.name}</div>
        <div style={styles.muted}>Loading...</div>
      </div>
    );
  }

  const shifts = coverage ? ['All', ...Array.from(new Set(coverage.dealers.map(d => d.shift).filter(Boolean)))] : ['All'];
  const shownDealers = coverage ? coverage.dealers.filter(d => shiftFilter === 'All' || d.shift === shiftFilter) : [];
  const lowCoverage = open && coverage && open.days_remaining < 7 && coverage.coverage_pct < 80;

  return (
    <div style={{ ...styles.card, opacity: game.active === false ? 0.6 : 1 }}>
      <div style={styles.cardHeader}>
        <div>
          <div style={styles.cardTitle}>{game.name}</div>
          {game.floor_visible === false && <span style={styles.badgeOff}>HIDDEN FROM FLOOR APP</span>}
          {custom ? (
            <>
              <span style={styles.badgeCustom}>CUSTOM</span>
              {game.active === false && <span style={styles.badgeOff}>ARCHIVED</span>}
              <div style={styles.statusLine}>
                Copy of {game.parent_name}. Measured against {game.parent_name}'s benchmark. Tests of custom
                games never change a benchmark.
              </div>
            </>
          ) : open ? (
            <>
              <div style={styles.badgeOn}>BENCHMARK MODE</div>
              <div style={styles.statusLine}>
                Day {Math.min(open.day, open.target_days)} of {open.target_days}
                {open.overdue ? ' — past target end' : ` — ${open.days_remaining} day(s) remaining`}
                {' · '}{open.vintage_label}
              </div>
              <div style={styles.muted}>Started {fmtDate(open.started_at)}. Every new test of this game counts toward the benchmark.</div>
              <div style={styles.progressOuter}>
                <div style={{ ...styles.progressInner, width: `${Math.min(100, (open.day / open.target_days) * 100)}%` }} />
              </div>
            </>
          ) : (
            <>
              <div style={styles.badgeOff}>STANDARD MODE</div>
              <div style={styles.statusLine}>
                {current
                  ? `Benchmark: ${current.vintage_label} (frozen), closed ${fmtDate(current.closed_at)}${current.n_dealers ? `, ${current.n_dealers} dealers` : ''}.`
                  : 'No benchmark period yet. Benchmarks are using live averages.'}
              </div>
            </>
          )}
        </div>
        <div style={styles.actions}>
          {custom ? (
            isAdmin && (game.active === false ? (
              <button style={styles.secondaryBtn} disabled={busy}
                onClick={() => call(`${API}/admin/games/${game.id}`, 'PATCH', { active: true }, 'Game restored', refreshAll)}>
                Restore
              </button>
            ) : (
              <>
                <button style={styles.secondaryBtn} onClick={() => togglePanel('rename')}>Rename</button>
                <button style={styles.dangerBtn} disabled={busy} onClick={() => {
                  if (window.confirm(`Archive ${game.name}? It will be hidden from the floor app. Its data is kept and it can be restored.`)) {
                    call(`${API}/admin/games/${game.id}`, 'PATCH', { active: false }, 'Game archived', refreshAll);
                  }
                }}>Archive</button>
              </>
            ))
          ) : open ? (
            <>
              <button style={styles.secondaryBtn} onClick={() => togglePanel('coverage')}>
                {panel === 'coverage' ? 'Hide Coverage' : 'View Coverage'}
              </button>
              {isAdmin && (
                <>
                  <button style={styles.primaryBtn} onClick={() => togglePanel('review')}>Review &amp; Close</button>
                  <button style={styles.secondaryBtn} onClick={() => togglePanel('extend')}>Change Length</button>
                  <button style={styles.dangerBtn} onClick={() => togglePanel('cancel')}>Cancel Period</button>
                </>
              )}
            </>
          ) : (
            <>
              {periods.history.length > 0 && (
                <button style={styles.secondaryBtn} onClick={() => togglePanel('history')}>History</button>
              )}
              {isAdmin && (
                <button style={styles.primaryBtn} onClick={() => togglePanel('start')}>
                  {current ? 'Start New Benchmark Period' : 'Start Benchmark Period'}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {isAdmin && (
        <div style={{ ...styles.setupBox, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <span style={{ ...styles.label, marginBottom: '2px' }}>Floor app</span>
            <span style={styles.muted}>
              {game.floor_visible === false
                ? 'Hidden — observers cannot pick this game.'
                : 'Observers can pick this game when starting a test.'}
            </span>
          </div>
          <button
            style={game.floor_visible === false ? styles.primaryBtn : styles.secondaryBtn}
            disabled={busy}
            onClick={() => call(`${API}/admin/games/${game.id}`, 'PATCH',
              { floor_visible: game.floor_visible === false },
              game.floor_visible === false ? 'Game shown on the floor app' : 'Game hidden from the floor app',
              refreshAll)}
          >
            {game.floor_visible === false ? 'Show on Floor App' : 'Hide from Floor App'}
          </button>
        </div>
      )}

      <div style={styles.setupBox}>
        <div style={styles.setupHead}>
          <span style={{ ...styles.label, marginBottom: 0 }}>Game setup</span>
          {isAdmin && fields.length > 0 && (
            <button style={styles.smallBtn} onClick={() => togglePanel('setup')}>
              {panel === 'setup' ? 'Close' : 'Edit Setup'}
            </button>
          )}
        </div>
        {fields.length === 0
          ? <div style={styles.muted}>No setup options have been defined for this game yet.</div>
          : <SetupSummary snapshot={game.setup_snapshot} />}
      </div>

      {message && <div style={styles.success}>{message}</div>}

      {panel === 'setup' && (
        <div style={styles.panel}>
          <div style={styles.explain}>
            Choose how this game is set up on your floor. Every test records the setup it was played under, and
            each benchmark records the setup it was built on. Avoid changing the setup mid-year.
          </div>
          {fields.map(f => {
            const activeOpts = f.options.filter(o => o.active);
            const chosenRetired = f.options.filter(o => !o.active && (f.multi ? (draft[f.field_key] || []).includes(o.id) : draft[f.field_key] === o.id));
            const opts = [...activeOpts, ...chosenRetired];
            return (
              <div key={f.id} style={{ marginBottom: '14px' }}>
                <label style={styles.label}>{f.label}</label>
                {f.multi ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                    {opts.map(o => (
                      <label key={o.id} style={{ color: '#ffffff', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={(draft[f.field_key] || []).includes(o.id)}
                          onChange={() => toggleMulti(f.field_key, o.id)} />
                        {o.label}{!o.active && ' (retired)'}
                      </label>
                    ))}
                    {opts.length === 0 && <span style={styles.muted}>No options yet.</span>}
                  </div>
                ) : (
                  <select style={{ ...styles.input, minWidth: '260px' }} value={draft[f.field_key] || ''}
                    onChange={e => setDraft(p => ({ ...p, [f.field_key]: e.target.value }))}>
                    <option value="">Not set</option>
                    {opts.map(o => <option key={o.id} value={o.id}>{o.label}{!o.active ? ' (retired)' : ''}</option>)}
                  </select>
                )}
              </div>
            );
          })}
          <div style={{ marginBottom: '16px' }}>
            <label style={styles.label}>Notes (optional)</label>
            <textarea style={{ ...styles.input, width: '100%', maxWidth: '560px', minHeight: '60px', fontFamily: 'inherit' }}
              value={notes} maxLength={500} onChange={e => setNotes(e.target.value)}
              placeholder="Anything else about how this game runs on your floor" />
          </div>
          {setupWarning && (
            <div style={styles.warning}>
              {setupWarning}
              <div style={{ ...styles.actions, marginTop: '10px' }}>
                <button style={styles.primaryBtn} disabled={busy} onClick={() => saveSetup(true)}>Save Anyway</button>
                <button style={styles.secondaryBtn} onClick={() => setSetupWarning('')}>Back</button>
              </div>
            </div>
          )}
          {error && <div style={styles.error}>{error}</div>}
          {!setupWarning && (
            <div style={styles.actions}>
              <button style={styles.primaryBtn} disabled={busy} onClick={() => saveSetup(false)}>{busy ? 'Saving...' : 'Save Setup'}</button>
              <button style={styles.secondaryBtn} onClick={() => setPanel(null)}>Back</button>
            </div>
          )}
        </div>
      )}

      {panel === 'rename' && (
        <div style={styles.panel}>
          <div style={styles.formRow}>
            <div>
              <label style={styles.label}>Name</label>
              <input style={{ ...styles.input, width: '280px' }} value={newName} maxLength={60}
                onChange={e => setNewName(e.target.value)} />
            </div>
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <div style={styles.actions}>
            <button style={styles.primaryBtn} disabled={busy || !newName.trim()}
              onClick={() => call(`${API}/admin/games/${game.id}`, 'PATCH', { name: newName }, 'Game renamed', refreshAll)}>
              Save
            </button>
            <button style={styles.secondaryBtn} onClick={() => setPanel(null)}>Back</button>
          </div>
        </div>
      )}

      {panel === 'start' && (
        <div style={styles.panel}>
          <div style={styles.explain}>
            While the period is open, every test of <b>{game.name}</b> counts toward the benchmark. Aim to test
            every active dealer at least 3 times, with at least 8 rounds per test. When the period ends you will
            review the results and freeze the new benchmark. The current game setup will be recorded with it.
          </div>
          <div style={styles.formRow}>
            <div>
              <label style={styles.label}>Length (days)</label>
              <input style={{ ...styles.input, width: '100px' }} type="number" min="1" max="120"
                value={days} onChange={e => setDays(e.target.value)} />
            </div>
            <div>
              <label style={styles.label}>Label</label>
              <input style={{ ...styles.input, width: '260px' }} value={label} onChange={e => setLabel(e.target.value)} />
            </div>
          </div>
          {current && (
            <div style={{ marginBottom: '16px' }}>
              <label style={styles.label}>Reason for new benchmark (required)</label>
              <input style={{ ...styles.input, width: '100%', maxWidth: '520px' }} value={reason}
                placeholder="e.g. Annual re-benchmark, new shuffler installed"
                onChange={e => setReason(e.target.value)} />
            </div>
          )}
          {error && <div style={styles.error}>{error}</div>}
          <div style={styles.actions}>
            <button style={styles.primaryBtn} disabled={busy || (current && !reason.trim())}
              onClick={() => call(`${API}/admin/games/${game.id}/benchmark-periods`, 'POST',
                { target_days: Number(days), vintage_label: label, reason }, 'Benchmark Period started', refreshAll)}>
              {busy ? 'Starting...' : 'Start Period'}
            </button>
            <button style={styles.secondaryBtn} onClick={() => setPanel(null)}>Back</button>
          </div>
        </div>
      )}

      {panel === 'extend' && open && (
        <div style={styles.panel}>
          <div style={styles.explain}>Set the total length of this period, counted from its start on {fmtDate(open.started_at)}.</div>
          <div style={styles.formRow}>
            <div>
              <label style={styles.label}>Total length (days)</label>
              <input style={{ ...styles.input, width: '100px' }} type="number" min="1" max="120"
                value={days} onChange={e => setDays(e.target.value)} />
            </div>
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <div style={styles.actions}>
            <button style={styles.primaryBtn} disabled={busy}
              onClick={() => call(`${API}/admin/benchmark-periods/${open.id}`, 'PATCH', { target_days: Number(days) },
                'Period length updated', refreshAll)}>
              {busy ? 'Saving...' : 'Save'}
            </button>
            <button style={styles.secondaryBtn} onClick={() => setPanel(null)}>Back</button>
          </div>
        </div>
      )}

      {panel === 'cancel' && open && (
        <div style={styles.panel}>
          <div style={styles.explain}>
            Cancelling ends this period without creating a benchmark. All tests collected during it move to
            Standard. They still count for each dealer's own results. This cannot be undone.
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <div style={styles.actions}>
            <button style={styles.dangerBtn} disabled={busy}
              onClick={() => call(`${API}/admin/benchmark-periods/${open.id}/cancel`, 'POST', null,
                d => `Period cancelled. ${d.sessions_reverted} test(s) moved to Standard.`, refreshAll)}>
              {busy ? 'Cancelling...' : 'Yes, Cancel Period'}
            </button>
            <button style={styles.secondaryBtn} onClick={() => setPanel(null)}>Keep Period Open</button>
          </div>
        </div>
      )}

      {panel === 'review' && open && (
        <div style={styles.panel}>
          {error && <div style={styles.error}>{error}</div>}
          {!preview && !error && <div style={styles.muted}>Calculating the proposed benchmark...</div>}
          {preview && (
            <>
              {preview.can_close && (
                <div style={styles.statsGrid}>
                  <div style={styles.stat}>
                    <div style={styles.statNum}>{preview.coverage.coverage_pct}%</div>
                    <div style={styles.statLbl}>Coverage</div>
                  </div>
                  <div style={styles.stat}>
                    <div style={styles.statNum}>{preview.n_dealers}</div>
                    <div style={styles.statLbl}>Dealers qualified</div>
                  </div>
                  <div style={styles.stat}>
                    <div style={styles.statNum}>{preview.n_sessions}</div>
                    <div style={styles.statLbl}>Counting tests</div>
                  </div>
                  <div style={styles.stat}>
                    <div style={styles.statNum}>{preview.n_rounds}</div>
                    <div style={styles.statLbl}>Rounds</div>
                  </div>
                </div>
              )}
              {preview.warnings && preview.warnings.length > 0 && (
                <div style={styles.warning}>{preview.warnings.map(w => <div key={w}>{w}</div>)}</div>
              )}
              {preview.can_close && (
                <>
                  <div style={{ marginBottom: '12px' }}>
                    <span style={styles.label}>Built on this setup</span>
                    <SetupSummary snapshot={preview.setup_snapshot} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={styles.label}>Proposed benchmark (seconds)</span>
                    <label style={{ ...styles.muted, display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={showCells} onChange={e => setShowCells(e.target.checked)} />
                      Show detail by table conditions
                    </label>
                  </div>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Code</th>
                        <th style={styles.th}>Measures</th>
                        <th style={styles.th}>Current</th>
                        <th style={styles.th}>Proposed</th>
                        <th style={styles.th}>Change</th>
                        <th style={styles.th}>Dealers</th>
                        <th style={styles.th}>Rounds</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.codes.slice().sort(codeSort).map(c => {
                        const change = c.current ? ((c.proposed - c.current) / c.current) * 100 : null;
                        return (
                          <React.Fragment key={c.code}>
                            <tr>
                              <td style={{ ...styles.td, fontWeight: 'bold' }}>{c.code}{c.source === 'pooled' ? ' *' : ''}</td>
                              <td style={styles.td}>{CODE_DESCRIPTIONS[c.code] || ''}</td>
                              <td style={styles.td}>{c.current != null ? c.current.toFixed(2) : '—'}</td>
                              <td style={{ ...styles.td, fontWeight: 'bold' }}>{c.proposed.toFixed(2)}</td>
                              <td style={styles.td}>{change != null ? `${change >= 0 ? '+' : ''}${change.toFixed(1)}%` : '—'}</td>
                              <td style={styles.td}>{c.n_dealers}</td>
                              <td style={styles.td}>{c.n_rounds}</td>
                            </tr>
                            {showCells && c.cells.map(cell => (
                              <tr key={cellLabel(cell)}>
                                <td style={styles.tdSub} colSpan="2">{cellLabel(cell)}</td>
                                <td style={styles.tdSub}>{cell.current != null ? cell.current.toFixed(2) : '—'}</td>
                                <td style={styles.tdSub}>{cell.proposed.toFixed(2)}</td>
                                <td style={styles.tdSub}>
                                  {cell.current ? `${cell.proposed >= cell.current ? '+' : ''}${(((cell.proposed - cell.current) / cell.current) * 100).toFixed(1)}%` : '—'}
                                </td>
                                <td style={styles.tdSub}>{cell.n_dealers}</td>
                                <td style={styles.tdSub}>{cell.n_rounds}</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  {preview.hph && preview.hph.length > 0 && (
                    <>
                      <div style={{ ...styles.label, marginTop: '20px' }}>Proposed HPH / RPH by SPR</div>
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            <th style={styles.th}>SPR</th>
                            <th style={styles.th}>Current HPH</th>
                            <th style={styles.th}>Proposed HPH</th>
                            <th style={styles.th}>Current RPH</th>
                            <th style={styles.th}>Proposed RPH</th>
                            <th style={styles.th}>Dealers</th>
                            <th style={styles.th}>Tests</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.hph.map(h => (
                            <tr key={h.spr}>
                              <td style={{ ...styles.td, fontWeight: 'bold' }}>{h.spr}</td>
                              <td style={styles.td}>{h.current_hph ?? '—'}</td>
                              <td style={{ ...styles.td, fontWeight: 'bold' }}>{h.target_hph ?? '—'}</td>
                              <td style={styles.td}>{h.current_rph ?? '—'}</td>
                              <td style={{ ...styles.td, fontWeight: 'bold' }}>{h.target_rph ?? '—'}</td>
                              <td style={styles.td}>{h.n_dealers}</td>
                              <td style={styles.td}>{h.n_sessions}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div style={{ ...styles.muted, marginTop: '8px' }}>
                        Applies to this game and its EZ/full version together. SPRs with fewer than 2 tests keep using
                        live averages.
                      </div>
                    </>
                  )}
                  <div style={{ ...styles.muted, margin: '10px 0 20px' }}>
                    Each dealer counts once (the average of each dealer's average), so dealers tested more often don't
                    pull the benchmark their way. * Too few rounds per dealer for that (e.g. shuffles), so the plain
                    average of all rounds is used. Conditions without enough data use the broader value.
                  </div>
                  {!confirmClose ? (
                    <div style={styles.actions}>
                      <button style={styles.primaryBtn} onClick={() => setConfirmClose(true)}>Close and Freeze Benchmark</button>
                      <button style={styles.secondaryBtn} onClick={() => setPanel(null)}>Back</button>
                    </div>
                  ) : (
                    <div style={styles.warning}>
                      This sets the benchmark for {game.name} until the next Benchmark Period. It cannot be undone.
                      New tests will be Standard from now on.
                      <div style={{ ...styles.actions, marginTop: '10px' }}>
                        <button style={styles.primaryBtn} disabled={busy}
                          onClick={() => call(`${API}/admin/benchmark-periods/${open.id}/close`, 'POST', { confirm: true },
                            'Benchmark frozen', refreshAll)}>
                          {busy ? 'Freezing...' : 'Yes, Freeze Benchmark'}
                        </button>
                        <button style={styles.secondaryBtn} onClick={() => setConfirmClose(false)}>Back</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {panel === 'history' && (
        <div style={styles.panel}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Label</th>
                <th style={styles.th}>Dates</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Dealers</th>
                <th style={styles.th}>Coverage</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {periods.history.map(h => (
                <React.Fragment key={h.id}>
                  <tr>
                    <td style={styles.td}>{h.vintage_label}</td>
                    <td style={styles.td}>{fmtDate(h.started_at)} – {fmtDate(h.closed_at)}</td>
                    <td style={styles.td}>{h.status === 'closed' ? 'Frozen' : 'Cancelled'}</td>
                    <td style={styles.td}>{h.n_dealers ?? '—'}</td>
                    <td style={styles.td}>{h.coverage_pct != null ? `${h.coverage_pct}%` : '—'}</td>
                    <td style={styles.td}>
                      {h.status === 'closed' && (
                        <button style={styles.smallBtn} onClick={() => loadHistoryValues(h.id)}>
                          {historyValues[h.id] ? 'Hide values' : 'View values'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {historyValues[h.id] && (historyValues[h.id].results || []).filter(r => !r.v1).map(r => (
                    <tr key={h.id + r.code}>
                      <td style={styles.tdSub} colSpan="2">{r.code} — {CODE_DESCRIPTIONS[r.code] || ''}</td>
                      <td style={styles.tdSub} colSpan="4">{Number(r.target_seconds).toFixed(2)} s · {r.n_dealers} dealers · {r.n_rounds} rounds</td>
                    </tr>
                  ))}
                  {historyValues[h.id] && (historyValues[h.id].hph || []).map(r => (
                    <tr key={h.id + 'spr' + r.spr}>
                      <td style={styles.tdSub} colSpan="2">SPR {Number(r.spr)}</td>
                      <td style={styles.tdSub} colSpan="4">HPH {r.target_hph ?? '—'} · RPH {r.target_rph ?? '—'} · {r.n_dealers} dealers · {r.n_sessions} tests</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {panel === 'coverage' && open && (
        <div style={styles.panel}>
          {!coverage ? (
            <div style={styles.muted}>Loading coverage...</div>
          ) : (
            <>
              {lowCoverage && (
                <div style={styles.warning}>
                  Less than a week left and only {coverage.coverage_pct}% of active dealers have qualified.
                  Consider testing more dealers or extending the period.
                </div>
              )}
              <div style={styles.statsGrid}>
                <div style={styles.stat}>
                  <div style={styles.statNum}>{coverage.coverage_pct}%</div>
                  <div style={styles.statLbl}>Coverage</div>
                </div>
                <div style={styles.stat}>
                  <div style={styles.statNum}>{coverage.qualified_dealers} / {coverage.active_dealers}</div>
                  <div style={styles.statLbl}>Dealers qualified</div>
                </div>
                <div style={styles.stat}>
                  <div style={styles.statNum}>{coverage.tests}</div>
                  <div style={styles.statLbl}>Benchmark tests</div>
                </div>
                <div style={styles.stat}>
                  <div style={styles.statNum}>{coverage.deal_rounds}</div>
                  <div style={styles.statLbl}>Rounds collected</div>
                </div>
              </div>
              <div style={{ ...styles.muted, marginBottom: '12px' }}>
                A dealer qualifies with {coverage.min_tests} tests of at least {coverage.min_rounds_per_test} rounds each.
              </div>
              {shifts.length > 2 && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={styles.label}>Shift</label>
                  <select style={styles.input} value={shiftFilter} onChange={e => setShiftFilter(e.target.value)}>
                    {shifts.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Dealer</th>
                    <th style={styles.th}>Payroll #</th>
                    <th style={styles.th}>Shift</th>
                    <th style={styles.th}>Counting tests</th>
                    <th style={styles.th}>Rounds</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {shownDealers.map(d => (
                    <tr key={d.dealer_id}>
                      <td style={styles.td}>{d.name}{!d.active && <span style={styles.muted}> (inactive)</span>}</td>
                      <td style={styles.td}>{d.payroll_number}</td>
                      <td style={styles.td}>{d.shift}</td>
                      <td style={styles.td}>
                        {d.counting_tests} of {coverage.min_tests}
                        {d.tests > d.counting_tests && <span style={styles.muted}> (+{d.tests - d.counting_tests} too short)</span>}
                      </td>
                      <td style={styles.td}>{d.deal_rounds}</td>
                      <td style={{ ...styles.td, color: STATUS_COLOR[d.status], fontWeight: 'bold' }}>{STATUS_LABEL[d.status]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function BenchmarkMode() {
  const isAdmin = localStorage.getItem('admin_role') === 'admin';
  const token = localStorage.getItem('admin_token');
  const [games, setGames] = useState(null);
  const [catalog, setCatalog] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addParent, setAddParent] = useState('');
  const [addName, setAddName] = useState('');
  const [addError, setAddError] = useState('');
  const [adding, setAdding] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  function loadGames() {
    const headers = { Authorization: `Bearer ${token}` };
    fetch(`${API}/admin/games`, { headers })
      .then(r => r.json())
      .then(data => setGames(Array.isArray(data) ? data : []))
      .catch(() => setGames([]));
    fetch(`${API}/admin/setup-catalog`, { headers })
      .then(r => r.json())
      .then(data => setCatalog(data && typeof data === 'object' ? data : {}))
      .catch(() => {});
  }

  // The shapes a brand new game can be built from.
  const [templates, setTemplates] = useState([]);
  const [showBuild, setShowBuild] = useState(false);
  const [buildTemplate, setBuildTemplate] = useState('');
  const [buildName, setBuildName] = useState('');
  const [building, setBuilding] = useState(false);
  const [buildError, setBuildError] = useState('');

  useEffect(() => {
    loadGames();
    fetch(`${API}/admin/game-templates`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setTemplates(d.templates || []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function buildGame() {
    setBuilding(true);
    setBuildError('');
    try {
      const res = await fetch(`${API}/admin/games/from-template`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ template: buildTemplate, name: buildName }),
      });
      if (res.ok) {
        setShowBuild(false);
        setBuildName('');
        loadGames();
      } else {
        setBuildError(await readError(res, 'Could not build the game'));
      }
    } catch (e) {
      setBuildError('Could not connect to server');
    }
    setBuilding(false);
  }

  const official = (games || []).filter(g => !g.is_custom);
  const customs = (games || []).filter(g => g.is_custom && g.active !== false);
  const archived = (games || []).filter(g => g.is_custom && g.active === false);

  async function addGame() {
    setAdding(true);
    setAddError('');
    try {
      const res = await fetch(`${API}/admin/games`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ parent_game_type_id: addParent, name: addName }),
      });
      if (res.ok) {
        setShowAdd(false);
        setAddName('');
        loadGames();
      } else {
        setAddError(await readError(res, 'Could not add the game'));
      }
    } catch (e) {
      setAddError('Could not connect to server');
    }
    setAdding(false);
  }

  function renderCard(game) {
    return (
      <GameCard key={game.id} game={game} fields={catalog[game.kind] || []}
        token={token} isAdmin={isAdmin} onChanged={loadGames} />
    );
  }

  return (
    <div style={styles.container}>
      <Sidebar active="Benchmarks" />
      <div style={styles.main}>
        <BenchmarkTabs current="games" />
        <div style={styles.pageTitle}>Games &amp; Benchmarks</div>
        <div style={styles.pageSubtitle}>
          Set up each game, and run a Benchmark Period to set its benchmark. During a period every test of that
          game counts toward the benchmark; at the end the benchmark is frozen until the next period. All other
          tests are Standard: they count for each dealer's results but do not change the benchmark.
        </div>

        {isAdmin && templates.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            {!showBuild ? (
              <button style={styles.primaryBtn} onClick={() => {
                setShowBuild(true);
                setBuildTemplate(templates[0]?.key || '');
                setBuildError('');
              }}>
                + Build a New Game
              </button>
            ) : (
              <div style={styles.card}>
                <div style={styles.cardTitle}>Build a New Game</div>
                <div style={styles.explain}>
                  Start from a template and give the game your own name &mdash; Three Card Poker, Ultimate Texas
                  Hold'em. It becomes a game in its own right: its own tests, its own reports, its own benchmark
                  and its own Benchmark Period, measured against nothing else. Pick the template whose steps match
                  how the game is actually dealt.
                </div>
                <div style={styles.formRow}>
                  <div>
                    <label style={styles.label}>Template</label>
                    <select style={{ ...styles.input, minWidth: '260px' }} value={buildTemplate}
                      onChange={e => setBuildTemplate(e.target.value)}>
                      {templates.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={styles.label}>Name</label>
                    <input style={{ ...styles.input, width: '260px' }} value={buildName} maxLength={60}
                      placeholder="e.g. Three Card Poker" onChange={e => setBuildName(e.target.value)} />
                  </div>
                </div>
                {templates.filter(t => t.key === buildTemplate).map(t => (
                  <div key={t.key} style={{ ...styles.explain, marginTop: '4px' }}>
                    <b>{t.description}.</b> The observer taps: {t.steps.join(' \u2192 ')}
                    {t.variables.length > 0 && <>, choosing {t.variables.join(', ').toLowerCase()} along the way</>}.
                  </div>
                ))}
                {buildError && <div style={styles.error}>{buildError}</div>}
                <div style={styles.actions}>
                  <button style={styles.primaryBtn} disabled={building || !buildTemplate || !buildName.trim()}
                    onClick={buildGame}>
                    {building ? 'Building...' : 'Build Game'}
                  </button>
                  <button style={styles.secondaryBtn} onClick={() => setShowBuild(false)}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        {isAdmin && (
          <div style={{ marginBottom: '20px' }}>
            {!showAdd ? (
              <button style={styles.secondaryBtn} onClick={() => {
                setShowAdd(true);
                setAddParent(official[0]?.id || '');
                setAddError('');
              }}>
                + Add Custom Game
              </button>
            ) : (
              <div style={styles.card}>
                <div style={styles.cardTitle}>Add Custom Game</div>
                <div style={styles.explain}>
                  A custom game is a copy of one of your games, with the same screens and rules, under its own name
                  (for example "High Limit BJ"). Its tests are kept separate so you can compare groups. Custom games
                  never change a benchmark: they are measured against the original game's benchmark.
                </div>
                <div style={styles.formRow}>
                  <div>
                    <label style={styles.label}>Copy of</label>
                    <select style={{ ...styles.input, minWidth: '220px' }} value={addParent} onChange={e => setAddParent(e.target.value)}>
                      {official.filter(g => g.active !== false).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={styles.label}>Name</label>
                    <input style={{ ...styles.input, width: '260px' }} value={addName} maxLength={60}
                      placeholder="e.g. High Limit BJ" onChange={e => setAddName(e.target.value)} />
                  </div>
                </div>
                {addError && <div style={styles.error}>{addError}</div>}
                <div style={styles.actions}>
                  <button style={styles.primaryBtn} disabled={adding || !addParent || !addName.trim()} onClick={addGame}>
                    {adding ? 'Adding...' : 'Add Game'}
                  </button>
                  <button style={styles.secondaryBtn} onClick={() => setShowAdd(false)}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        {games === null && <div style={styles.muted}>Loading...</div>}
        {games && games.length === 0 && <div style={styles.muted}>No games found.</div>}
        {official.map(renderCard)}

        {customs.length > 0 && <div style={styles.sectionTitle}>Custom games</div>}
        {customs.map(renderCard)}

        {archived.length > 0 && (
          <>
            <button style={{ ...styles.smallBtn, marginTop: '16px' }} onClick={() => setShowArchived(v => !v)}>
              {showArchived ? 'Hide' : 'Show'} archived custom games ({archived.length})
            </button>
            {showArchived && <div style={{ marginTop: '16px' }}>{archived.map(renderCard)}</div>}
          </>
        )}
      </div>
    </div>
  );
}

export default BenchmarkMode;
